/* ⚠️ GENERATED from app/services/birthdays.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   🎂 birthdays.ts — whose birthday it is, where, and who may be told.
   One rule for the server (the hourly sweep that tells friends) and both
   clients (the ring around an avatar, the Privacy rows).

   WHERE A DATE OF BIRTH LIVES
     users/{uid}/private/dob  { dateOfBirth, monthDay: 'MM-DD', timeZone }
       — readable by its owner only (MERGED_RULES). The public user document
       carries `hasDob: true` instead of the date itself. Older accounts still
       have `dateOfBirth` on the public document until the server's migration
       copies it across (and, once BDAY_STRIP_PUBLIC is set, removes it).
     users/{uid}.bdayUntil    ms — the end of today's birthday in their time
       zone, written by the sweep only when the birthday may be seen. The ring
       around an avatar reads this, never the date.

   SETTINGS — users/{uid}.privacySettings (absent = the default)
     birthdayVisibility 'friends' | 'everyone' | 'nobody'   (default 'friends')
     birthdayAnnounce   false → friends are not told on my birthday
     birthdayAlerts     false → I am not told about friends' birthdays
   ═══════════════════════════════════════════════════════════════════════════ */

/** The local hour friends are told — morning where the birthday is. */
export const ANNOUNCE_HOUR = 9;
const DAY = 86400000;
export function birthdayVisibility(user) {
  const v = user && user.privacySettings && user.privacySettings.birthdayVisibility;
  return v === 'everyone' || v === 'nobody' ? v : 'friends';
}

/** Are my friends told on my birthday? Never when the birthday is hidden. */
export function announces(user) {
  return birthdayVisibility(user) !== 'nobody' && !(user && user.privacySettings && user.privacySettings.birthdayAnnounce === false);
}

/** Do I want to hear about my friends' birthdays? */
export function wantsAlerts(user) {
  return !(user && user.privacySettings && user.privacySettings.birthdayAlerts === false);
}

/** A Timestamp / Date / number / ISO string → ms, or null. */
export function dobMs(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.getTime() : null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  if (typeof v._seconds === 'number') return v._seconds * 1000;
  if (typeof v === 'string') {
    const n = Date.parse(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
const pad = n => n < 10 ? `0${n}` : String(n);

/**
 * 'MM-DD' of a date of birth. The pickers save LOCAL midnight, which in UTC is
 * the previous evening east of Greenwich and the same morning west of it —
 * rounding to the nearest UTC day gives back the day that was picked for every
 * offset between -12h and +12h.
 */
export function monthDayOf(dob) {
  const ms = dobMs(dob);
  if (ms == null) return null;
  const d = new Date(ms + DAY / 2);
  return `${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** The year of birth (same rounding). */
export function birthYearOf(dob) {
  const ms = dobMs(dob);
  if (ms == null) return null;
  return new Date(ms + DAY / 2).getUTCFullYear();
}

/** Minutes east of UTC that `timeZone` is at `atMs` (0 when unknown). */
export function tzOffsetMin(timeZone, atMs) {
  if (!timeZone) return 0;
  try {
    const f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    const parts = {};
    for (const p of f.formatToParts(new Date(atMs))) parts[p.type] = p.value;
    const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour) % 24, Number(parts.minute));
    return Math.round((asUtc - Math.floor(atMs / 60000) * 60000) / 60000);
  } catch {
    return 0;
  }
}

/** The local calendar day at `atMs` in `timeZone`: { key 'YYYY-MM-DD', md 'MM-DD', year, hour, start, end } (start/end in UTC ms). */
export function localDay(atMs, timeZone) {
  const off = tzOffsetMin(timeZone, atMs) * 60000;
  const local = new Date(atMs + off);
  const y = local.getUTCFullYear();
  const m = local.getUTCMonth() + 1;
  const d = local.getUTCDate();
  const start = Date.UTC(y, m - 1, d) - off;
  return {
    key: `${y}-${pad(m)}-${pad(d)}`,
    md: `${pad(m)}-${pad(d)}`,
    year: y,
    hour: local.getUTCHours(),
    start,
    end: start + DAY
  };
}
const isLeap = y => {
  if (y % 400 === 0) return true;
  if (y % 100 === 0) return false;
  return y % 4 === 0;
};

/** Is it this birthday today, where the person is? 29 Feb is kept on 28 Feb in other years. */
export function isBirthdayOn(monthDay, atMs, timeZone) {
  if (!monthDay) return false;
  const day = localDay(atMs, timeZone);
  if (day.md === monthDay) return true;
  return monthDay === '02-29' && !isLeap(day.year) && day.md === '02-28';
}

/** The 'MM-DD' values that can be "today" somewhere on Earth right now — what the sweep asks for. */
export function candidateMonthDays(atMs) {
  const out = new Set();
  for (const h of [-12, 0, 14]) {
    const d = new Date(atMs + h * 3600000);
    out.add(`${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`);
  }
  if (out.has('02-28')) out.add('02-29');
  return Array.from(out);
}

/** Does the ring show around this person's picture now? (bdayUntil is server-written) */
export function ringActive(user, nowMs) {
  const until = Number(user && user.bdayUntil);
  return Number.isFinite(until) && until > nowMs && nowMs >= until - DAY - 2 * 3600000;
}

/** The marker that makes the announcement once a year: birthdays/{uid}_{year}. */
export const markerId = (uid, year) => `${uid}_${year}`;
